import { Component } from '@theme/component';

/**
 * @typedef {object} Review
 * @property {string} title
 * @property {string} body
 * @property {string} name
 * @property {number} rating
 */

/**
 * @typedef {object} Refs
 * @property {HTMLElement} source - The hidden Okendo widget.
 * @property {HTMLElement} viewport - Revealed once reviews render.
 * @property {HTMLElement} track - The theme's slideshow-component.
 */

/**
 * Re-renders the hidden Okendo widget's reviews into the theme's slideshow-component,
 * which owns the drag, snap and arrow behaviour.
 *
 * @extends {Component<Refs>}
 */
class OkendoTestimonials extends Component {
  requiredRefs = ['source', 'viewport', 'track'];

  /** @type {MutationObserver | undefined} */
  #observer;

  #rendered = 0;

  #hydrated = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();

    this.#update(this.#fromJson(), false);

    this.#observer = new MutationObserver(() => this.#schedule());
    this.#observer.observe(this.refs.source, { childList: true, subtree: true });
    this.#schedule();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
  }

  /* Okendo mutates in bursts while hydrating. setTimeout, not rAF: rAF is paused
     in background tabs, so the carousel would stay empty until the tab is focused. */
  #schedule() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.#timer = undefined;
      this.#update(this.#fromDom(), true);
    }, 50);
  }

  /**
   * @returns {Review[]}
   */
  #fromJson() {
    const script = this.refs.source.querySelector('script[data-oke-metafield-data]');
    if (!script?.textContent) return [];

    try {
      /** @type {{ reviews?: Record<string, any>[] }} */
      const data = JSON.parse(script.textContent);
      return (data.reviews ?? [])
        .map((review) => ({
          title: review.title ?? '',
          body: review.body ?? '',
          name: review.reviewer?.displayName ?? '',
          rating: Number(review.rating) || 0,
        }))
        .filter((review) => review.body || review.title);
    } catch {
      return [];
    }
  }

  /**
   * @returns {Review[]}
   */
  #fromDom() {
    const cards = this.refs.source.querySelectorAll(
      '.oke-reviewCarousel-reviews-review, .oke-w-reviews-list-item'
    );

    return [...cards]
      .map((card) => {
        /** @param {string} selector */
        const text = (selector) => card.querySelector(selector)?.textContent?.trim() ?? '';
        const stars = card.querySelector('.oke-stars .oke-a11yText')?.textContent ?? '';

        return {
          title: text('.oke-reviewContent-title'),
          body: text('.oke-reviewContent-body'),
          name: text('.oke-w-reviewer-name'),
          rating: Number(stars.match(/([\d.]+)\s+out of/)?.[1]) || 0,
        };
      })
      .filter((review) => review.body || review.title);
  }

  /**
   * DOM order honours the merchant's sort (Featured first); the JSON blob is always
   * `date desc`, so it is a first paint only and the DOM supersedes it.
   *
   * @param {Review[]} reviews
   * @param {boolean} fromDom
   */
  #update(reviews, fromDom) {
    if (!reviews.length) return;

    const supersedes = fromDom && !this.#hydrated;
    if (!supersedes && reviews.length <= this.#rendered) return;
    if (fromDom) this.#hydrated = true;

    const { track, viewport } = this.refs;
    const label = this.dataset.ratingLabel ?? '';

    const slides = track.querySelector('slideshow-slides');
    if (!slides) return;

    slides.replaceChildren(
      ...reviews.map((review, index) => {
        const wrapper = document.createElement('slideshow-slide');
        wrapper.setAttribute('ref', 'slides[]');
        wrapper.setAttribute('slide-id', `oke-review-${index}`);
        wrapper.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');

        const slide = document.createElement('div');
        slide.className = 'oke-testimonial-carousel__slide';

        if (review.rating) {
          const stars = document.createElement('div');
          stars.className = 'oke-testimonial-carousel__stars';
          stars.style.setProperty('--rating', String(review.rating));

          const readout = document.createElement('span');
          readout.className = 'visually-hidden';
          readout.textContent = label.replace('%rating%', String(review.rating));
          stars.append(readout);
          slide.append(stars);
        }

        if (review.title) {
          const title = document.createElement('p');
          title.className = 'oke-testimonial-carousel__title h3';
          title.textContent = review.title;
          slide.append(title);
        }

        if (review.body) {
          const body = document.createElement('p');
          body.className = 'oke-testimonial-carousel__body';
          body.textContent = review.body;
          slide.append(body);
        }

        if (review.name) {
          const name = document.createElement('p');
          name.className = 'oke-testimonial-carousel__name text-body-s';
          name.textContent = review.name;
          slide.append(name);
        }

        wrapper.append(slide);
        return wrapper;
      })
    );

    this.#rendered = reviews.length;
    viewport.hidden = false;

    // Slideshow set itself up with zero slides on load; re-attaching re-runs that
    // with the slides present, so drag, snap and arrows all initialise.
    const slideshow = this.refs.track;
    const parent = slideshow.parentElement;
    const sibling = slideshow.nextSibling;
    slideshow.remove();
    parent?.insertBefore(slideshow, sibling);
  }
}

if (!customElements.get('okendo-testimonials')) {
  customElements.define('okendo-testimonials', OkendoTestimonials);
}
